import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bitcoin, X, TrendingUp, TrendingDown, RefreshCw, Loader } from 'lucide-react';
import { useCryptoPrices } from '../hooks/useCryptoPrices';
import { formatCurrency } from '../utils/currencyUtils';

interface PriceOverlayProps {
  isOpen: boolean;
  onClose: () => void;
}

export function PriceOverlay({ isOpen, onClose }: PriceOverlayProps) {
  const { prices, isLoading, error, refresh, lastUpdate } = useCryptoPrices();
  const [nextUpdate, setNextUpdate] = useState(10000); // 10 seconds

  // Update countdown timer
  useEffect(() => {
    if (!isOpen) return;

    const timer = setInterval(() => {
      setNextUpdate(prev => {
        if (prev <= 1000) {
          refresh(true);
          return 10000;
        }
        return prev - 1000;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [isOpen, refresh]);

  const price = prices?.BTC?.price;
  const priceChange = prices?.BTC?.change24h;
  const isPriceUp = priceChange !== null && priceChange >= 0;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -20 }}
          className="fixed top-20 right-4 z-50"
        >
          <div className="bg-white rounded-2xl shadow-xl border border-gray-200 w-80">
            {/* Header */}
            <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-orange-50 rounded-lg">
                  <Bitcoin className="w-5 h-5 text-orange-500" />
                </div>
                <h3 className="font-semibold text-gray-900">Bitcoin Price</h3>
              </div>
              <button
                onClick={onClose}
                className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>

            {/* Content */}
            <div className="p-4">
              {error ? (
                <div className="text-center text-red-600 text-sm">{error}</div>
              ) : (
                <div className="text-center space-y-2">
                  <div className="text-3xl font-bold text-gray-900">
                    {isLoading ? (
                      <div className="flex items-center justify-center">
                        <Loader className="w-6 h-6 animate-spin text-blue-500" />
                      </div>
                    ) : (
                      formatCurrency(price || 0, 'USD', { USD: 1 })
                    )}
                  </div>
                  {!isLoading && priceChange !== null && (
                    <div className={`flex items-center justify-center gap-1 text-sm ${
                      isPriceUp ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {isPriceUp ? (
                        <TrendingUp className="w-4 h-4" />
                      ) : (
                        <TrendingDown className="w-4 h-4" />
                      )}
                      {Math.abs(priceChange).toFixed(2)}%
                    </div>
                  )}
                </div>
              )}

              {/* Update Timer */}
              <div className="mt-4 flex items-center justify-between text-sm text-gray-500">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => refresh(true)}
                    disabled={isLoading}
                    className={`p-1 hover:bg-gray-100 rounded-full transition-colors ${
                      isLoading ? 'animate-spin' : ''
                    }`}
                  >
                    <RefreshCw className="w-4 h-4" />
                  </button>
                  Next update in {Math.ceil(nextUpdate / 1000)}s
                </div>
                <div>
                  {lastUpdate && (
                    <span>
                      Last: {lastUpdate.toLocaleTimeString()}
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}